<script>
import CoverSheetTemplateUploader from "@/components/document-validation/coverSheet/CoverSheetTemplateUploader.vue";
import LargeTabHeader from "@/components/common/tabs/LargeTabHeader.vue";

export default {
  components: {LargeTabHeader, CoverSheetTemplateUploader},
  data() {
    return {
      isUploadDialogOpen: false,
      isEditDialogOpen: false,
      isDeleteDialogOpen: false,
      editingTemplate: null,
      coverSheets: [],
      searchTerm: '',
      updatedTemplatePreview: 0
    };
  },
  computed: {
    filteredCoverSheets() {
      return this.coverSheets.filter(sheet =>
        this.searchTerm !== '' ? sheet.name.toLowerCase().includes(this.searchTerm.toLowerCase()) : true
      );
    }
  },
  created() {
    this.getCoverSheetsList();
  },
  methods: {
    getCoverSheetsList() {
      this.$coverSheetService.getCoverSheets().then((sheets) => {
        this.coverSheets = sheets;
        sheets?.forEach(template => {
          this.$coverSheetService.getCoverSheet(template.id).then(blob => {
            if (blob instanceof Blob) {
              template.imageUrl = URL.createObjectURL(blob);
            } else {
              console.error('Expected a Blob, but got:', blob);
            }
          }).catch(error => {
            console.error('Error fetching cover sheet:', error);
          }).finally(() => {
            this.updatedTemplatePreview++;
          });
        });
      });
    },
    getImageStyle(imageUrl) {
      return imageUrl
        ? `background:url(${imageUrl}) no-repeat center center;`
        : '';
    },
    deleteSheet(template) {
      this.$coverSheetService.deleteCoverSheet(template?.id).then(() => {
        this.coverSheets = this.coverSheets.filter(sheet => sheet.id !== template?.id);
        this.isDeleteDialogOpen = false;
      });
    },
    uploadedStationery() {
      this.isUploadDialogOpen = false;
      this.isEditDialogOpen = false;
      this.getCoverSheetsList();
    },
  },
};
</script>
<template>
  <v-container fluid>
    <!-- HEADER-->
    <LargeTabHeader
      :title="$t('component.profile.accountManagement.cover.sheets.header')"
      :subtitle="$t('component.profile.accountManagement.cover.sheets.subheader')" />

    <v-divider></v-divider>

    <!-- SEARCHBAR-->
    <v-row>
      <v-col cols="9">
        <v-text-field
          id="searchCoversheet"
          v-model="searchTerm"
          :label="$t('global.search.label')"
          type="text"
          append-icon="mdi-magnify"
          outlined
          dense
          required>
        </v-text-field>
      </v-col>
      <v-col cols="3">
        <!-- CREATE BUTTON-->
        <v-btn color="primary" @click="isUploadDialogOpen = true;">
          <v-icon>mdi-file-document-outline</v-icon>
          {{ $t('button.create.cover.sheet') }}
        </v-btn>
        <!-- CREATE DIALOG-->
        <v-dialog v-model="isUploadDialogOpen" max-width="500px">
          <v-card>
            <v-card-title class="card-title-dark">
              {{ $t('component.profile.accountManagement.create.template') }}
            </v-card-title>
            <CoverSheetTemplateUploader @stationery-uploaded="uploadedStationery"/>
          </v-card>
        </v-dialog>
      </v-col>
    </v-row>
    <!--    OUTPUT - SEARCH NO RESULTS-->
    <v-row v-if="filteredCoverSheets.length === 0">
      <v-col cols="12" class="text-center">
        {{ $t('component.profile.accountManagement.no.match') }}
      </v-col>
    </v-row>
    <!-- SEARCH RESULTS / TEMPLATES LIST-->
    <v-row v-else>
      <v-col
        v-for="(template, index) in filteredCoverSheets"
        :key="index + updatedTemplatePreview"
        cols="6">
        <v-card>
          <!-- IMAGE PREVIEW-->
          <div style="background-color:#f9f9f9;padding:18px;">
            <div
              :style="getImageStyle(template.imageUrl)"
              class="card-image-preview">
            </div>
          </div>
          <!-- TEMPLATE NAME / ACTIONS-->
          <v-card-title>{{ template.name }}</v-card-title>
          <v-card-actions class="justify-center">
            <v-row>
              <v-col sm="6">
                <v-btn block color="primary" @click="()=>{editingTemplate = {...template}; isEditDialogOpen = true}">
                  <v-icon>mdi-pencil</v-icon>
                  {{ $t('button.edit') }}
                </v-btn>
              </v-col>
              <v-col sm="6">
                <v-btn
                  block
                  color="red"
                  style="color:white"
                  @click="()=>{editingTemplate = {...template}; isDeleteDialogOpen = true}">
                  <v-icon>mdi-trash-can-outline</v-icon>
                  {{ $t('button.delete') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card-actions>
        </v-card>
        <!-- EDIT DIALOG-->
        <v-dialog
          v-model="isEditDialogOpen" max-width="500px" :open-on-focus="true"
          @click:outside="isEditDialogOpen = false">
          <v-card>
            <v-card-title class="card-title-dark">
              {{ $t('component.profile.accountManagement.edit.template') }}
            </v-card-title>
            <CoverSheetTemplateUploader
              v-if="editingTemplate"
              :id="editingTemplate.id ? editingTemplate.id : null"
              :key="editingTemplate.id"
              :template-file="editingTemplate.imageUrl ? editingTemplate.imageUrl : null"
              :name="editingTemplate.name ? editingTemplate.name : template.name"
              :mode="'edit'"
              @stationery-uploaded="uploadedStationery"/>
          </v-card>
        </v-dialog>
        <!-- DELETE DIALOG-->
        <v-dialog
          v-model="isDeleteDialogOpen" max-width="500px" :open-on-focus="true"
          @click:outside="isDeleteDialogOpen = false">
          <v-card>
            <v-card-title class="card-title-dark">
              {{ $t('component.profile.accountManagement.delete.template') }}
            </v-card-title>
            <v-card-text class="pb-0">
              <v-row>
                <v-col>
                  <span class="text-body-2">
                    {{ $t('component.profile.accountManagement.reassure.deletion') }}</span>
                </v-col>
              </v-row>
            </v-card-text>
            <v-card-actions class="justify-center">
              <v-row>
                <v-col>
                  <v-btn color="red" block style="color:white;" @click="deleteSheet(editingTemplate)">
                    <v-icon>mdi-check</v-icon>
                    {{ $t('global.yes') }}
                  </v-btn>
                </v-col>
                <v-col>
                  <v-btn color="primary" block @click="isDeleteDialogOpen = false">
                    <v-icon>mdi-close</v-icon>
                    {{ $t('global.no') }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-col>
    </v-row>
  </v-container>
</template>
<style scoped>
.card-title-dark {
  background: #002c38;
  color: white;
}

.card-image-preview {
  min-height: 60vh !important;
  background-size: contain !important;
  width: 100% !important;
}
</style>
